"use client";

import { JOIN_WAITLIST_URL } from "@/common/const";
import { useRouter } from "@bprogress/next/app";
import { Button, ConfigProvider, Input } from "antd";
import classNames from "classnames";
import Image from "next/image";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { useCallback, useEffect, useState, type ChangeEvent } from "react";
import logoIcon from "./assets/logo-icon.png";
import discordImg from "./assets/social-media-discord.png";
import googleImg from "./assets/social-media-google.png";
import styles from "./index.module.css";

export const INVITE_CODE_STORAGE_KEY = "vidmuse:invite-code";

const OAUTH_FUNCTION_PARAM = "vid_muse";
const INVALID_INVITE_REASON = "invalid_invitation_code";

type MaybeSearchParams = ReturnType<typeof useSearchParams>;

const sanitizeInviteCode = (code: string) => code.trim();

const readInviteFromParams = (params: MaybeSearchParams) => {
  const invite = params?.get("invite_code");
  return invite ? sanitizeInviteCode(invite) : "";
};

const isInvalidInviteAttempt = (params: MaybeSearchParams) =>
  params?.get("loginSuccess") === "false" &&
  params?.get("reason") === INVALID_INVITE_REASON;

const readStoredInviteCode = () => {
  if (typeof window === "undefined") {
    return "";
  }

  try {
    return sessionStorage.getItem(INVITE_CODE_STORAGE_KEY) ?? "";
  } catch (error) {
    console.error("Failed to read invite code from sessionStorage:", error);
    return "";
  }
};

const persistInviteCode = (code: string) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    if (code) {
      sessionStorage.setItem(INVITE_CODE_STORAGE_KEY, code);
      return;
    }
    sessionStorage.removeItem(INVITE_CODE_STORAGE_KEY);
  } catch (error) {
    console.error("Failed to persist invite code to sessionStorage:", error);
  }
};

const buildOAuthUrl = (provider: "google" | "discord", code: string) => {
  const params = new URLSearchParams();
  params.set("invite_code", code);
  params.set("function", OAUTH_FUNCTION_PARAM);
  return `/auth/oauth2/${provider}?${params.toString()}`;
};

export default function Login() {
  const router = useRouter();
  const searchParams = useSearchParams();

  const [inviteCode, setInviteCode] = useState("");
  const [hasInviteError, setHasInviteError] = useState(false);

  const applyInviteCode = useCallback((value: string) => {
    setInviteCode((previous) => (previous === value ? previous : value));
  }, []);

  useEffect(() => {
    const invalidAttempt = isInvalidInviteAttempt(searchParams);
    setHasInviteError(invalidAttempt);

    const inviteFromParams = readInviteFromParams(searchParams);
    if (inviteFromParams) {
      applyInviteCode(inviteFromParams);
      return;
    }

    if (invalidAttempt) {
      applyInviteCode(readStoredInviteCode());
    }
  }, [applyInviteCode, searchParams]);

  const handleInviteChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      applyInviteCode(event.target.value);
      setHasInviteError((previous) => (previous ? false : previous));
    },
    [applyInviteCode]
  );

  const handleAuthRedirect = useCallback(
    (provider: "google" | "discord") => {
      const sanitizedCode = sanitizeInviteCode(inviteCode);
      persistInviteCode(sanitizedCode);
      window.location.href = buildOAuthUrl(provider, sanitizedCode);
    },
    [inviteCode]
  );

  const handleGoogleLogin = useCallback(() => {
    handleAuthRedirect("google");
  }, [handleAuthRedirect]);

  const handleDiscordLogin = useCallback(() => {
    handleAuthRedirect("discord");
  }, [handleAuthRedirect]);

  const handleCloseBtnClick = useCallback(() => {
    router.push("/");
  }, [router]);

  return (
    <ConfigProvider
      theme={{
        token: {
          colorBorder: "rgba(16, 16, 16, 0.04)",
          borderRadius: 12,
          boxShadow: "none",
        },
        components: {
          Input: {
            fontSize: 14,
            paddingInline: 16,
            colorErrorBorder: "#f00",
          },
          Button: {
            defaultBorderColor: "rgba(16, 16, 16, 0.04)",
            borderRadiusLG: 12,
            defaultShadow: "none",
          },
        },
      }}
    >
      <div
        className={classNames(
          styles.container,
          "relative bg-white flex items-start sm:items-center justify-center min-h-screen"
        )}
      >
        {/* 主登录框 */}
        <div className="relative w-full sm:w-[480px] sm:rounded-3xl sm:border border-white sm:bg-white/30 sm:shadow-[0_0_40px_0_rgba(228,206,255,0.2)] backdrop-blur-[10px] px-5 sm:px-[60px] py-10 min-w-[360px]">
          <div className="flex items-center justify-center w-20 h-20 mb-3 mx-auto rounded-full border border-[rgba(0,0,0,0.08)] bg-white shadow-[0_3.45px_29.785px_0_rgba(0,123,255,0.03)] backdrop-blur-[5.75px]">
            <Image src={logoIcon} alt="VidMuse" width={48} height={45} />
          </div>

          {/* 标题和描述 */}
          <div className="mt-6 flex flex-col items-center">
            <h1 className="text-black text-2xl font-bold">
              Sign in to VidMuse
            </h1>
            <p className="mt-2 text-sm font-normal text-black opacity-60">
              Got an invite? Enter your code to unlock access
            </p>
          </div>

          {/* 邀请码输入框 */}
          <div className="flex flex-col items-center mt-10">
            <Input
              className={classNames(
                "rounded-3xl! h-12 font-medium",
                styles.inviteInput
              )}
              placeholder="Enter your invitation code"
              value={inviteCode}
              onChange={handleInviteChange}
              status={hasInviteError ? "error" : undefined}
              aria-invalid={hasInviteError}
              aria-describedby={hasInviteError ? "invite-error" : undefined}
              maxLength={40}
            />
            {hasInviteError ? (
              <p className="mt-3 text-xs text-red-500 text-center">
                This invite code is invalid or has already been used.
              </p>
            ) : null}

            {!searchParams?.get("invite_code") && !hasInviteError && (
              <p className="mt-3 text-xs text-black/60 text-center">
                <span>Don&apos;t have an invite code? 👉 </span>
                <Link
                  target="_blank"
                  href={JOIN_WAITLIST_URL}
                  className="text-black! font-medium underline! hover:text-[#5251F6]!"
                >
                  Join waitlist NOW
                </Link>
              </p>
            )}
          </div>

          {/* 分割线 */}
          <div className="flex items-center gap-3 mx-auto my-10">
            <div className="flex-1 h-[0.5px] bg-black/20"></div>
            <span className="text-xs font-normal text-black/20">Continue</span>
            <div className="flex-1 h-[0.5px] bg-black/20"></div>
          </div>

          <ConfigProvider
            theme={{
              components: {
                Button: {
                  defaultBorderColor: "rgba(0, 0, 0, 0.04)",
                  borderRadiusLG: 24,
                },
              },
            }}
          >
            {/* 社交登录按钮 */}
            <div className="flex flex-col gap-3">
              <Button
                size="large"
                icon={
                  <Image width={16} height={16} src={googleImg} alt="Google" />
                }
                onClick={handleGoogleLogin}
              >
                Sign in with Google
              </Button>

              <Button
                size="large"
                icon={
                  <Image
                    width={16}
                    height={16}
                    src={discordImg}
                    alt="Discord"
                  />
                }
                onClick={handleDiscordLogin}
              >
                Sign in with Discord
              </Button>
            </div>
          </ConfigProvider>

          {/* 服务条款 */}
          <p className="mt-10 text-xs font-normal leading-normal text-black/60 text-center w-full sm:w-[360px]">
            <span>By signing in, you are agreeing to the </span>
            <Link
              className="text-black! font-medium underline! hover:text-[#5251F6]!"
              href="/terms-of-service"
            >
              Terms of Service
            </Link>
            <span>, Acceptable </span>
            <Link
              className="text-black! font-medium underline! hover:text-[#5251F6]!"
              href="/use-policy"
            >
              Use Policy
            </Link>
            <span>, </span>
            <Link
              className="text-black! font-medium underline! hover:text-[#5251F6]!"
              href="/dispute-policy"
            >
              Dispute policy
            </Link>
            <span> and </span>
            <Link
              className="text-black! font-medium underline! hover:text-[#5251F6]!"
              href="/privacy-policy"
            >
              Privacy Policy.
            </Link>
          </p>

          {/* 关闭按钮 */}
          <button
            className="absolute top-5 right-5 w-5 h-5 flex items-center justify-center bg-transparent border-none cursor-pointer text-[#98A2B3] hover:opacity-70"
            onClick={handleCloseBtnClick}
            aria-label="Close login dialog"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M12 4L4 12M4 4L12 12"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
    </ConfigProvider>
  );
}
